import { useLingui } from '@lingui/react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import Button from '../components/Button';
import Input from '../components/Input';
import Select from '../components/Select';

import AddElementBar from '../components/AddElementBar';
import CoverImageSection from '../components/CoverImageSection';
import ImageSection from '../components/ImageSection';
import LinkSection from '../components/LinkSection';
import TextSection from '../components/TextSection';

import RichTextEditor from '../components/RichTextEditor';
import { useNewsEditor } from '../hooks/useNewsEditor';
import { NewsCategory } from '../types/news';

export default function NewsPage() {
  const { i18n } = useLingui();
  const navigate = useNavigate();

  const {
    elements,
    title,
    setTitle,
    selectedCategory,
    setSelectedCategory,
    shortDescription,
    setShortDescription,
    coverImageUrls,
    coverImageUploading,
    noCoverImage,
    moveElement,
    publishState,
    isValid,
    lastSavedId,
    handleCoverImageUpload,
    handleToggleNoCoverImage,
    handleImageUpload,
    updateElement,
    deleteElement,
    addTextSection,
    addLinkSection,
    addImageSection,
    handlePublish,
  } = useNewsEditor();

  useEffect(() => {
    if (lastSavedId) navigate(`/news/edit/${lastSavedId}`);
  }, [lastSavedId, navigate]);

  const isSaving = publishState.status === 'loading';

  return (
    <div className="container-page padding-top-8 padding-bottom-8">
      <div className="col-xs-12 col-lg-6">
        <div
          className="flex flex-col items-start gap-1 grow-1 w-full"
          style={{ maxWidth: '600px' }}
        >
          <h1>{i18n._('news.create')}</h1>

          <CoverImageSection
            noCoverImage={noCoverImage}
            coverImageUrls={coverImageUrls}
            coverImageUploading={coverImageUploading}
            onToggleNoCoverImage={handleToggleNoCoverImage}
            onUpload={handleCoverImageUpload}
          />

          <div className="flex flex-col w-half">
            <label>{i18n._('news.category')}</label>
            <Select
              disabled={isSaving}
              placeholder={i18n._('news.categoryPlaceholder')}
              options={[
                { label: i18n._('news.category.news'), value: NewsCategory.NEWS },
                { label: i18n._('news.category.events'), value: NewsCategory.EVENTS },
                { label: i18n._('news.category.updates'), value: NewsCategory.UPDATES },
              ]}
              onChange={setSelectedCategory}
            />
          </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full margin-top-2">
            <h3>{i18n._('news.titleLabel')}</h3>
            <div className="flex flex-col">
              <label>{i18n._('news.titleInput')}</label>
              <Input
                disabled={isSaving}
                value={title}
                maxlength={120}
                onChange={(value) => setTitle(value ?? '')}
                placeholder={i18n._('news.titlePlaceholder')}
              />
            </div>
          </div>

          <div className="border border-gray-tint-6 rounded-_20 padding-3 w-full margin-top-2">
            <h3>{i18n._('news.shortDescription')}</h3>
            <div className="flex flex-col gap-2">
              <label>{i18n._('news.shortDescriptionLabel')}</label>
              <RichTextEditor
                disabled={isSaving}
                value={shortDescription}
                onChange={(value) => setShortDescription(value)}
                placeholder={i18n._('news.shortDescriptionPlaceholder')}
              />
            </div>
          </div>

          {elements.map((element, index) => {
            if (element.type === 'text') {
              return (
                <TextSection
                  key={element.id}
                  element={element}
                  onUpdate={updateElement}
                  onDelete={deleteElement}
                  onMoveUp={() => moveElement(index, 'up')}
                  onMoveDown={() => moveElement(index, 'down')}
                  isFirst={index === 0}
                  isLast={index === elements.length - 1}
                />
              );
            }
            if (element.type === 'link') {
              return (
                <LinkSection
                  key={element.id}
                  element={element}
                  onUpdate={updateElement}
                  onDelete={deleteElement}
                  onMoveUp={() => moveElement(index, 'up')}
                  onMoveDown={() => moveElement(index, 'down')}
                  isFirst={index === 0}
                  isLast={index === elements.length - 1}
                />
              );
            }
            if (element.type === 'image') {
              return (
                <ImageSection
                  key={element.id}
                  element={element}
                  onUpload={handleImageUpload}
                  onDelete={deleteElement}
                  onMoveUp={() => moveElement(index, 'up')}
                  onMoveDown={() => moveElement(index, 'down')}
                  isFirst={index === 0}
                  isLast={index === elements.length - 1}
                />
              );
            }
            return null;
          })}

          <AddElementBar
            onAddText={addTextSection}
            onAddImage={addImageSection}
            onAddLink={addLinkSection}
          />

          <div className="flex gap-2 margin-top-2">
            <Button theme="secondary" disabled={isSaving}>{i18n._('common.cancel')}</Button>
            <Button
              onClick={handlePublish}
              disabled={!isValid || isSaving}
            >
              {isSaving
                ? i18n._('common.publishing')
                : i18n._('common.publish')}
            </Button>
          </div>
          {publishState.status === 'success' && lastSavedId && (
            <p style={{ color: 'green' }}>{i18n._('news.saveSuccess')}</p>
          )}
          {publishState.status === 'error' && (
            <p style={{ color: 'red' }}>
              {i18n._('news.publishError', { message: publishState.message })}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
